import * as React from "react";
import {
  Typography, Button, Chip, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Checkbox, FormControlLabel, Collapse,
  Box, Paper, Stack
} from "../../components/mui";
import {
  RestartAltRoundedIcon, GetAppRoundedIcon, AddRoundedIcon,
  ApartmentRoundedIcon, ExpandMoreRoundedIcon, ExpandLessRoundedIcon
} from "../../components/icons";

import "../../styles/main.css";
import "../../styles/pages/viewOccupancy.css";
import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import CondoUnitFormModal from "../condo-units/modals/AddCondoUnitModal";
import OccupantsHistoryModal from "../condo-units/modals/OccupantsHistoryModal";
import { COLUMN_OPTIONS, FILTERS, OCCUPANCY_ROWS } from "./viewOccupancyMocks";

const FILTER_MENU_PROPS = {
  PaperProps: {
    className: "view-occupancy-select-menu"
  },
  MenuListProps: {
    className: "view-occupancy-select-menu-list"
  }
};

/**
 * View Occupancy Page Component
 * UI/Layout only - Backend developer will add:
 * - API calls for fetching occupancy data
 * - Filter functionality
 * - Column visibility state management
 * - Export functionality
 */
export default function ViewOccupancy({ onLogout, onNavigate }) {
  // Modal state
  const [columnSelectionOpen, setColumnSelectionOpen] = React.useState(false);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [occupantsHistoryModalOpen, setOccupantsHistoryModalOpen] = React.useState(false);
  const [selectedUnit, setSelectedUnit] = React.useState(null);

  const handleViewOccupantsHistory = (unitData) => {
    setSelectedUnit(unitData);
    setOccupantsHistoryModalOpen(true);
  };

  return (
    <PageLayout activeKey="view-occupancy" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <PageHeader
        icon={ApartmentRoundedIcon}
        title="View Condo Occupancy"
        summary="Monitor inventory, leasing, and occupancy trends for every property."
        summaryExtra={
          <Box className="view-occupancy-summary">
            <Typography variant="body2" className="view-occupancy-summary-label">
              Summary:
            </Typography>
            <Typography variant="body2" className="view-occupancy-summary-item">
              <strong>Capacity</strong> 4
            </Typography>
            <Typography variant="body2" className="view-occupancy-summary-item">
              <strong>Actual</strong> 2
            </Typography>
            <Typography variant="body2" className="view-occupancy-summary-item">
              <strong>Available</strong> 2
            </Typography>
          </Box>
        }
        actions={
          <>
            <Button variant="outlined" startIcon={<RestartAltRoundedIcon />}>
              Reset
            </Button>
            <Button variant="outlined" startIcon={<GetAppRoundedIcon />}>
              Export
            </Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setAddModalOpen(true)}>
              Add condo unit
            </Button>
          </>
        }
      />

      <Paper elevation={0} className="content-box view-occupancy-toolbar" aria-label="Occupancy filters">
        <Box className="view-occupancy-filter-grid">
          {FILTERS.map(({ id, label, defaultValue, options }) => (
            <FormControl key={id} fullWidth size="small" className="view-occupancy-select">
              <InputLabel id={id}>{label}</InputLabel>
              <Select labelId={id} label={label} defaultValue={defaultValue} MenuProps={FILTER_MENU_PROPS}>
                {options.map(({ value, label: optionLabel }) => (
                  <MenuItem key={value} value={value}>
                    {optionLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="text"
            className="view-occupancy-toggle-columns"
            onClick={() => setColumnSelectionOpen((prev) => !prev)}
            endIcon={columnSelectionOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          >
            Column selection
          </Button>
        </Stack>

        <Collapse in={columnSelectionOpen} className="view-occupancy-columns">
          <Box className="view-occupancy-columns-grid">
            {COLUMN_OPTIONS.map(({ id, label, defaultChecked }) => (
              <FormControlLabel
                key={id}
                control={<Checkbox defaultChecked={defaultChecked} color="primary" />}
                label={label}
              />
            ))}
          </Box>
        </Collapse>
      </Paper>

      <DataTableCard
        title="Condo Units List"
        showExport={false}
        totalEntries={4}
        startEntry={1}
        endEntry={4}
        currentPage={1}
        totalPages={1}
      >
        <TableContainer className="data-table-wrapper data-table-wrapper--comfortable">
          <Table size="medium" className="view-occupancy-table">
            <TableHead>
              <TableRow>
                <TableCell>Unit</TableCell>
                <TableCell>Condo</TableCell>
                <TableCell>Lessee</TableCell>
                <TableCell>Lessor</TableCell>
                <TableCell align="right">Capacity</TableCell>
                <TableCell align="right">Staff Occupants</TableCell>
                <TableCell align="right">Non-Staff Occupants</TableCell>
                <TableCell className="action-cell">Occupants History</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Condo Company</TableCell>
                <TableCell>Condo Sharing</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {OCCUPANCY_ROWS.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>{row.condo}</TableCell>
                  <TableCell>{row.lessee}</TableCell>
                  <TableCell>{row.lessor}</TableCell>
                  <TableCell align="right">{row.capacity}</TableCell>
                  <TableCell align="right">{row.staffOccupants}</TableCell>
                  <TableCell align="right">{row.nonStaffOccupants}</TableCell>
                  <TableCell className="action-cell">
                    <Button
                      variant="text"
                      size="small"
                      className="action-button"
                      onClick={() => handleViewOccupantsHistory({ unit: row.unit, condo: row.condo })}
                    >
                      View
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.activeLabel}
                      color={row.activeStatus || "default"}
                      className="view-occupancy-status-chip"
                    />
                  </TableCell>
                  <TableCell>{row.condoCompany}</TableCell>
                  <TableCell>{row.condoSharing}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTableCard>

      {/* Add Condo Unit Modal - Reused from condo-units */}
      <CondoUnitFormModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />

      {/* Occupants History Modal - Reused from condo-units */}
      <OccupantsHistoryModal
        open={occupantsHistoryModalOpen}
        onClose={() => setOccupantsHistoryModalOpen(false)}
        unitData={selectedUnit}
      />
    </PageLayout>
  );
}
